import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Mode } from '@amzn/awsui-global-styles';

import { EMBED_TO_PARENT_MESSAGES, PARENT_TO_EMBED_MESSAGES } from '../constants/embedMessageConstants';
import { useTheme } from '../contexts/ThemeContext';
import { useEmbedParams } from './useEmbedParams';
import { appendEmbedParamsToPath, buildEmbedReportedPath, isSafeReportedPath } from '../utils/navigationUtils';
import { isFinSuiteOrigin, resolveFinSuiteParentOrigin } from '../utils/finsuiteOrigin';
import { logger } from '../utils/logger';

/**
 * Listens for postMessage events from the FinSuite parent window
 * and syncs theme changes into Verity's ThemeContext.
 *
 * Also sends an EMBED_READY signal so FinSuite knows it can start
 * sending messages.
 *
 * IMPORTANT: This hook must be used inside a <BrowserRouter> context
 * so that useNavigate() works for client-side navigation (no full reload).
 */
export const useParentMessageListener = (): void => {
  const { isEmbedded } = useEmbedParams();
  const { setMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const setModeRef = useRef(setMode);
  const navigateRef = useRef(navigate);

  // Keep refs in sync to avoid stale closures in the listener
  useEffect(() => {
    setModeRef.current = setMode;
  }, [setMode]);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  // Report the current in-app path to the parent so it can deep-link its external
  // "open in new tab" link and reflect the path in its URL hash. Fires on mount and on every
  // route change. Embed plumbing params are stripped so they don't leak upstream.
  useEffect(() => {
    if (!isEmbedded) return;

    try {
      // Target the specific FinSuite parent origin rather than '*': the reported path can
      // include report IDs / query params, so broadcasting it to any embedding page is a leak.
      // Fail closed when the parent isn't a recognized FinSuite origin (or can't be resolved,
      // e.g. Firefox has no ancestorOrigins).
      const targetOrigin = resolveFinSuiteParentOrigin();
      if (!targetOrigin) {
        logger?.debug('Skipping FINSUITE_CURRENT_PATH — parent is not a recognized FinSuite origin');
        return;
      }

      const path = buildEmbedReportedPath(location.pathname, location.search, location.hash);
      window.parent.postMessage({ type: EMBED_TO_PARENT_MESSAGES.CURRENT_PATH, payload: { path } }, targetOrigin);
      // Debug-level: fires once per navigation, so at info it would be a Katal log line per
      // click with no error signal. The catch below stays at error.
      logger?.debug(`Sent FINSUITE_CURRENT_PATH to ${targetOrigin}: ${path}`);
    } catch (error) {
      logger?.error('Failed to send current path to parent', { error });
    }
  }, [isEmbedded, location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (!isEmbedded) return;

    const handleMessage = (event: MessageEvent) => {
      const { type, payload } = event.data || {};
      // Ignore messages that aren't our protocol
      if (!type) return;

      // Every parent→embed message below causes a side effect (navigation or theme change), so
      // verify the sender is a genuine FinSuite origin once here. Without this, any page that
      // frames Verity could post FINSUITE_NAVIGATE_HOME / _TO / _THEME_CHANGE and drive it.
      if (!isFinSuiteOrigin(event.origin)) {
        logger?.warn(`Ignoring parent message from non-FinSuite origin: ${event.origin}`);
        return;
      }

      switch (type) {
        case PARENT_TO_EMBED_MESSAGES.THEME_CHANGE: {
          const isDarkMode = payload?.isDarkMode;
          if (typeof isDarkMode === 'boolean') {
            logger?.info('Received theme change from parent:', { isDarkMode });
            setModeRef.current(isDarkMode ? Mode.Dark : Mode.Light);
          }
          break;
        }
        case PARENT_TO_EMBED_MESSAGES.NAVIGATE_HOME: {
          logger?.info('Received navigate home from parent');
          navigateRef.current(appendEmbedParamsToPath('/'));
          break;
        }
        case PARENT_TO_EMBED_MESSAGES.NAVIGATE_TO: {
          // Origin is already verified above. Validate the target path (the parent derives it
          // from a user-pasted URL hash) before routing.
          const targetPath = payload?.path;
          if (!isSafeReportedPath(targetPath)) {
            logger?.warn(`Ignoring FINSUITE_NAVIGATE_TO with unsafe path: ${JSON.stringify(targetPath)}`);
            break;
          }
          logger?.info(`Received navigate-to from parent: ${targetPath}`);
          navigateRef.current(appendEmbedParamsToPath(targetPath));
          break;
        }
        default:
          break;
      }
    };

    window.addEventListener('message', handleMessage);

    // Signal to FinSuite that we're ready to receive messages
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ type: EMBED_TO_PARENT_MESSAGES.EMBED_READY }, '*');
      logger?.info('Sent EMBED_READY to parent');
    }

    // Forward clicks to parent so FinSuite can close dropdowns (e.g. search)
    let handleClick: (() => void) | undefined;
    if (isEmbedded) {
      let lastClickForward = 0;
      handleClick = () => {
        const now = Date.now();
        if (now - lastClickForward < 100) return; // Throttle: max once per 100ms
        lastClickForward = now;
        if (window.parent && window.parent !== window) {
          window.parent.postMessage({ type: EMBED_TO_PARENT_MESSAGES.IFRAME_CLICK }, '*');
        }
      };
      document.addEventListener('mousedown', handleClick);
    }

    return () => {
      window.removeEventListener('message', handleMessage);
      if (handleClick) document.removeEventListener('mousedown', handleClick);
    };
  }, [isEmbedded]);
};
